import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isVisibleAiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { usePatientConversationsData } from './useConversationsData'
import { useEligibleParticipants } from './useEligibleParticipants'
import { getClientFullName } from '../../../../common/utils/utils'
import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { useGuardianForSelectedClient } from '../../../../hooks/useGuardianForSelectedClient'
import { useSelectedClientData } from '../../../clients/clientDetails/data/hooks/useSelectedClientData'
import { getResolvedConversations } from '../messagesSelectors'
import { buildMessengerThreads } from '../threadUtils'
import { MessengerThread } from '../types'

export const useMessengerThreads = () => {
  const providerId = useSelector(getAuthUserId)
  const clientId = useSelector(getSelectedClientId)
  const { isPreferredNameEnabled } = useFlags()
  const resolvedConversations = useSelector(getResolvedConversations)

  const {
    // `data` is the last result for ANY arg, so mid client-switch it is still the previous client's.
    // currentData is only defined once the response for THIS client lands, which is what callers
    // must wait for before concluding a thread has no conversation.
    currentData: patientConversationsForClient,
    isFetching: isFetchingPatientConversations,
  } = usePatientConversationsData(
    clientId ? { providerId, patientId: clientId, types: CONVERSATION_TYPE.PROVIDER } : skipToken,
  )
  const {
    currentData: aiConversationsForClient,
    isError: isAiConversationsError,
    isFetching: isFetchingAiConversations,
  } = usePatientConversationsData(
    clientId ? { providerId, patientId: clientId, types: CONVERSATION_TYPE.AI_COACH } : skipToken,
    { refetchOnMountOrArgChange: true },
  )
  // RTK can retain data from the previous args, making isLoading false while the current client is still fetching.
  const isLoadingPatientConversations = isFetchingPatientConversations && patientConversationsForClient === undefined
  const isLoadingAiConversations = isFetchingAiConversations && aiConversationsForClient === undefined

  // RTK Query starts refetchOnMountOrArgChange in an effect. A cached result therefore looks
  // settled for one render before its forced refetch begins. Track that this client has actually
  // entered the request cycle before allowing an absent exact AI target to fall back.
  const [aiRequestLifecycle, setAiRequestLifecycle] = useState({
    clientId: null as string | null,
    hasStarted: false,
  })
  useEffect(() => {
    setAiRequestLifecycle((current) => {
      if (current.clientId !== clientId) {
        return { clientId, hasStarted: isFetchingAiConversations }
      }
      if (isFetchingAiConversations && !current.hasStarted) {
        return { ...current, hasStarted: true }
      }
      return current
    })
  }, [clientId, isFetchingAiConversations])
  const { data: selectedClientDetails } = useSelectedClientData()
  const { guardian, loadingGuardian } = useGuardianForSelectedClient()
  const guardianLyraId = guardian.guardianLyraId
  const eligibleParticipants = useEligibleParticipants()

  // The guardian conversation carries patient_lyra_id === guardianLyraId, so keep both ids. Merge
  // locally resolved secondary-provider conversations until the patient-scoped refetch contains them.
  const patientAndGuardianConversations = useMemo(() => {
    const fetchedClientAndGuardianConversations =
      patientConversationsForClient?.filter(
        (conversation) => conversation.patient_lyra_id === clientId || conversation.patient_lyra_id === guardianLyraId,
      ) ?? []
    const fetchedConversationIds = new Set(
      fetchedClientAndGuardianConversations.map((conversation) => conversation.conversation_id),
    )
    return [
      ...fetchedClientAndGuardianConversations,
      ...resolvedConversations.filter(
        (conversation) =>
          conversation.anchor_patient_lyra_id === clientId && !fetchedConversationIds.has(conversation.conversation_id),
      ),
    ]
  }, [patientConversationsForClient, clientId, guardianLyraId, resolvedConversations])
  const visibleAiConversations = useMemo(
    () =>
      aiConversationsForClient?.filter(
        (conversation) => conversation.patient_lyra_id === clientId && isVisibleAiConversation(conversation),
      ) ?? [],
    [aiConversationsForClient, clientId],
  )

  const patientFirstName = useMemo(
    () =>
      (isPreferredNameEnabled
        ? (selectedClientDetails?.preferred_first_name ?? selectedClientDetails?.first_name)
        : selectedClientDetails?.first_name) ?? '',
    [isPreferredNameEnabled, selectedClientDetails?.preferred_first_name, selectedClientDetails?.first_name],
  )
  const guardianFirstName = guardian.guardianFirstName ?? ''
  const patientFullName = selectedClientDetails ? getClientFullName(selectedClientDetails, isPreferredNameEnabled) : ''
  const guardianFullName = guardian.guardianName
  const threads = useMemo<MessengerThread[]>(() => {
    if (!clientId) {
      return []
    }
    return buildMessengerThreads({
      clientId,
      patientAndGuardianConversations,
      aiConversations: visibleAiConversations,
      eligibleParticipants,
      patientFirstName,
      guardianFirstName,
      patientFullName,
      guardianFullName,
      guardianLyraId,
    })
  }, [
    clientId,
    patientAndGuardianConversations,
    visibleAiConversations,
    patientFirstName,
    guardianFirstName,
    patientFullName,
    guardianFullName,
    guardianLyraId,
    eligibleParticipants,
  ])

  return {
    threads,
    isLoading: isLoadingPatientConversations || isLoadingAiConversations || loadingGuardian,
    // Only sendable patient/guardian placeholders need the creation guard. An AI request failure
    // must not prevent a provider from starting an ordinary conversation.
    threadsLoaded: patientConversationsForClient !== undefined,
    aiThreadsLoaded:
      aiRequestLifecycle.clientId === clientId &&
      aiRequestLifecycle.hasStarted &&
      !isFetchingAiConversations &&
      (aiConversationsForClient !== undefined || isAiConversationsError),
  }
}
